import React from "react";
import { styled, Button, Typography, IconButton, Box } from "@mui/material";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { addYears, isAfter } from "date-fns";
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import Select from 'components/form-inputs/select';
import FormSectionTitle from "components/menu-drawer/form-section-title";
import DateTimePicker from 'components/form-inputs/date-time-picker';
import Icon from 'components/icon';


const FieldContainerBox = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(2), 
  marginTop: theme.spacing(0.5), 
  marginBottom: theme.spacing(1), 
  flexGrow: 2, 
  maxWidth: 250,
}));


export default function VetVisitEditVaccinations(props) {
  const { vaccinationCoreTypes, vaccinationNonCoreTypes, drawerState, translation } = props;
  const { t } = useTranslation(translation);
  const formContext = useFormContext();
  const { control, formState: { dirtyFields }, getValues, resetField } = formContext;
  const { fields, append, remove } = useFieldArray({
    name: 'vaccinations',
    keyName: 'hookFormId',
  });
  const dateValue = useWatch({ control, name: 'date' });
  const vaccinationTypeOptions = [
    {isHeader: true, label: 'Core'},
    ...vaccinationCoreTypes,
    { isHeader: true, label: 'Non-core' },
    ...vaccinationNonCoreTypes,
  ]


  const handleAddClick = () => {
    append({
      id: null,
      vaccinationTypeId: null,
      dateNextVaccination: null,
    });
  };


  const handleTypeChange = (value, idx) => {
    // Auto-set the next vaccination date (3 years for core, 1 for non-core) if it hasn't already been customized.
    if (!dirtyFields.vaccinations?.[0]?.dateNextVaccination) {
      const yearsToNext = vaccinationCoreTypes.find(t => t.id === value)
        ? 3
        : 1;
      // Use today's date if the date field is empty.
      const dateVal = getValues('date');
      const appointmentDate = (dateVal && !isNaN(new Date(dateVal))) ? dateVal : new Date();
      const next = addYears(appointmentDate, yearsToNext);
      // Mark this value as the new default so we can use dirtyFields to see if it has been customized.
      resetField(`vaccinations[${idx}].dateNextVaccination`, { defaultValue: next });
    }
  };


  const handleRemove = (idx) => {
    // Reset the default values when removing 
    resetField(`vaccinations[${idx}].dateNextVaccination`, { defaultValue: null });
    remove(idx);
  };


  return (
    <EditorIconSection
      iconName='vaccination'
      helpProps={{
        translation,
        section: 'vaccinations'
      }}
    >
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('vaccinations.vaccinations.helpTitle')}
        text={t('vaccinations.vaccinations.helpInfo')}
      >
        {focusHandlers =>
          <>
            <FormSectionTitle sx={{ mb: 1 }}>Vaccinations</FormSectionTitle>
            <Typography variant='subtext-small' sx={{ mb: 3 }}>
              {t('vaccinations.vaccinations.helpInfo')}
            </Typography>            
            {fields.map((field, idx) =>
              <Box
                key={field.hookFormId}
                sx={{ marginBottom: 2, display: 'flex' }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', flexGrow: 2 }}>
                  <FieldContainerBox sx={{ minWidth: 180 }}>
                    <Select
                      name={`vaccinations[${idx}].vaccinationTypeId`}
                      label={t('vaccinations.vaccinations.labelType')}
                      focusHandlers={focusHandlers}
                      //defaultValue=''
                      options={vaccinationTypeOptions}
                      //setValueAs={id => ({ id })}
                      rules={{ 
                        required: t('vaccinations.vaccinations.errMsgTypeEmpty'),
                        validate: value => {
                          const firstIdx = fields.findIndex(field => field.vaccinationTypeId === value);
                          if (firstIdx > -1 && firstIdx !== idx) {
                            return t('vaccinations.vaccinations.errMsgTypeDuplicate');
                          }
                          return true;
                        }
                      }}
                      onChange={(e, value) => handleTypeChange(value, idx)}
                    />
                  </FieldContainerBox>

                  <FieldContainerBox sx={{ minWidth: 125 }}>
                    <DateTimePicker
                      variant='date'
                      name={`vaccinations[${idx}].dateNextVaccination`}
                      label={t('vaccinations.vaccinations.labelDate')}
                      focusHandlers={focusHandlers}
                      rules={{ 
                        required: t('vaccinations.vaccinations.errMsgDateEmpty'),
                        validate: val => (isAfter(
                          new Date(val).setHours(0, 0, 0, 0), 
                          new Date(dateValue).setHours(0, 0, 0, 0))
                        ) || t('vaccinations.vaccinations.errMsgDateBefore'),
                      }}
                      sx={{ marginTop: `0 !important` }}  // TODO: refine default hoverable text field spacing so this isn't required
                    />
                  </FieldContainerBox>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1 }}>
                  <IconButton
                    onClick={() => handleRemove(idx)}
                    size="small">
                    <Icon type='delete' />
                  </IconButton>                  
                </Box>

              </Box>
            )}
            <div>
              <Button
                variant='outlined'
                onClick={handleAddClick}
                startIcon={<Icon type='vaccination' />}
              >
                Add Vaccination
              </Button>
            </div>
          </>
        }
      </HelpTextItem>
    </EditorIconSection>
  );

}